import * as Sentry from "@sentry/react";

import { getHostEnvironment } from "client/src/utils/getHostEnvironment";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in tsdocs
import { handlePreloadErrors } from "client/src/utils/preloadError";
import { useEffect } from "react";

import { DSN } from "shared/data/Sentry";
import { isLocalDev, isTest } from "shared/utils/config";
import { assertIsDefined } from "shared/utils/utils";
import type { User, setUser } from "@sentry/react";
import type { InjectedEnv } from "shared/types/Env";

export function initSentry(): void {
  if (isLocalDev || isTest) {
    console.log("Sentry is disabled in local development.");
    return;
  }

  const environment = getHostEnvironment();

  const commitHashMetaTag = document.querySelector<HTMLMetaElement>('meta[name="COMMIT_HASH"]');
  assertIsDefined(commitHashMetaTag, "commitHashMetaTag");
  const release =
    commitHashMetaTag.content === "%COMMIT_HASH%" ? "none" : commitHashMetaTag.content;

  Sentry.init({
    dsn: DSN,
    tunnel: "/api/sentry",
    release,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    normalizeDepth: 6,

    enableLogs: true,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: (
      {
        local: 1.0,
        "qa-dcs": 1.0,
        "stage-dcs": 1.0,
        "prod-dcs": 0.1,
        unknown: 0.0,
      } as const
    )[environment],
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", window.location.host],

    ignoreErrors: [
      /**
       * Ignore errors that are caused by dynamically imported modules that fail to fetch
       * when they are deleted from the hosting server after a newer deployment has gone out.
       *
       * This is a common issue with code splitting and we can ignore them because we
       * immediately hard-refresh the page when this error is triggered.
       *
       * @see {@link handlePreloadErrors}
       */
      /Failed to fetch dynamically imported module/, // Chrome error message
      /error loading dynamically imported module/, // Firefox error message
      /Unable to preload CSS for/,
    ],
  });

  registerCSPViolationLogging();
}

// Capture Content-Security-Policy violations that fire in the browser and report
// them to Sentry. CSP violations do not throw JS errors, so without this listener
// they would only surface in the server-side report-uri endpoint.
function registerCSPViolationLogging() {
  const seen = new Set<string>();

  document.addEventListener("securitypolicyviolation", (event) => {
    const key = `${event.effectiveDirective}|${event.blockedURI}`;
    if (seen.has(key)) return;
    seen.add(key);

    Sentry.captureMessage(
      `CSP violation: ${event.effectiveDirective} blocked ${event.blockedURI}`,
      {
        level: "error",
        tags: {
          "csp.directive": event.effectiveDirective,
          "csp.disposition": event.disposition,
        },
        extra: {
          documentURI: event.documentURI,
          referrer: event.referrer,
          violatedDirective: event.violatedDirective,
          blockedURI: event.blockedURI,
          sourceFile: event.sourceFile,
          lineNumber: event.lineNumber,
          columnNumber: event.columnNumber,
          statusCode: event.statusCode,
          sample: event.sample,
        },
      },
    );
  });
}

export function useConfigureSentryScope(config: InjectedEnv | undefined) {
  useEffect(() => {
    if (config) {
      Sentry.withScope((scope) => {
        scope.addEventProcessor(function (event) {
          event.release = config.COMMIT_HASH;
          event.environment = getHostEnvironment();
          return event;
        });
      });
    }
  }, [config]);
}

let existingSentryUser: User | null = null;

export const setSentryUser: typeof setUser = (user) => {
  const isDifferent = JSON.stringify(existingSentryUser) !== JSON.stringify(user);
  if (isDifferent) {
    existingSentryUser = user;
    Sentry.withScope((scope) => {
      scope.setUser(user);
    });
  }
};
